import type { DashboardFilters } from '@delivery-metrics/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { DashboardV2ApiService } from '../services/dashboard-v2-api';
import {
  compositeToLeadershipOverview,
  compositeToMetricSeries,
  compositeToPredictabilityPoints,
} from '../utils/compositeAdapter';

export const DASHBOARD_COMPOSITE_QUERY_KEY = 'dashboard-v2-composite';

/**
 * The one request the top-level page makes for its figures (US-22).
 *
 * It replaces six: `getKPIs`, `getLeadershipKPIs` and one call per metric,
 * all of which the KPI section used to issue from a `useEffect` of its own.
 * With this hook the section is handed its data and initiates nothing.
 *
 * **`placeholderData: keepPreviousData` is a requirement, not a nicety.**
 * Applying a filter must not blank the page: without it the query key
 * changes, React Query reports `isPending` with no data, and the section
 * falls back to its skeleton — every card disappears and returns, for every
 * filter change. Keeping the previous result means the old figures stay on
 * screen, marked stale via `isPlaceholderData`, until the new ones arrive.
 *
 * No `refetchInterval`, for the reason US-23 removed the others: the figures
 * move once per collector cycle.
 */
export function useDashboardComposite(filters: DashboardFilters) {
  const query = useQuery({
    queryKey: [DASHBOARD_COMPOSITE_QUERY_KEY, filters],
    queryFn: () => DashboardV2ApiService.getComposite(filters),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: (failureCount, error: any) => {
      if (error?.response?.status >= 400 && error?.response?.status < 500) {
        return false;
      }
      return failureCount < 3;
    },
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  const composite = query.data ?? null;

  return {
    composite,
    /** The card values, units and fitted trend lines, in the section's shape. */
    leadershipKPIs: composite ? compositeToLeadershipOverview(composite) : null,
    /** The chart series per signal. */
    metricsData: composite ? compositeToMetricSeries(composite) : null,
    /** PI and Release readings per bucket, for the On Time / Predictability card. */
    predictabilityPoints: composite
      ? compositeToPredictabilityPoints(composite)
      : null,
    /** True only with nothing to show; a filter change keeps the old view. */
    isLoading: query.isPending,
    /** True while the figures on screen belong to the previous filters. */
    isStaleView: query.isPlaceholderData,
    error: query.error,
    refetch: query.refetch,
  };
}
